import {ComponentType, useState} from 'react';
import {BodySite, BodySiteSelector, Condition} from '@legit-health/ui';
import {Box, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import {
  FULL_BODY_SITE,
  NO_CONDITION
} from '@/shared/components/diagnosticReport/DiagnosticReportForm/const/noCondition';
import {BasePatient} from '../../types';
import BottomBar from '../BottomBar';
import ConditionSelector from './components/ConditionSelector';
import PathologySelector from './components/PathologySelector';

export type Value = {
  bodySiteId: string | null;
  conditionId: string | null;
  visitIdentifier: string;
  pathologyId: string | null;
  fullBody: boolean;
};

export default function SelectPathologyStep({
  bodySites,
  conditions,
  visitIdentifierComponent: VisitIdentifierComponent,
  onContinue,
  patient,
  value: initialValue,
  t,
  isForClinicalTrials = false
}: {
  bodySites: BodySite[];
  conditions: Condition[];
  visitIdentifierComponent?: ComponentType<{
    onChange: (visitIdentifier: string) => void;
    value: string;
  }>;
  onContinue: (value: Value) => void;
  patient: BasePatient;
  t: TFunction;
  value: Value;
  isForClinicalTrials?: boolean;
}) {
  const [value, setValue] = useState<Value>(initialValue);

  function handleSelectPathology(pathologyId: string | null) {
    setValue((v) => ({
      ...v,
      pathologyId
    }));
  }

  function handleSelectCondition(conditionId: string | null) {
    setValue((v) => ({
      ...v,
      bodySiteId: null,
      conditionId,
      fullBody: conditionId === FULL_BODY_SITE
    }));
  }

  function handleSelectBodySite(value: string | null) {
    setValue((v) => ({
      ...v,
      bodySiteId: value
    }));
  }

  function handleChangeExtraComponent(visitIdentifier: string) {
    setValue((v) => ({
      ...v,
      visitIdentifier
    }));
  }

  function handleNext() {
    onContinue(value);
  }

  return (
    <Box
      sx={{
        maxWidth: 'md',
        mx: 'auto'
      }}
    >
      <Typography
        variant="h4"
        align="center"
        sx={{
          mb: '16px'
        }}
      >
        {t('shared/common:selectPathology')}
      </Typography>
      <Box sx={{mb: 2}} data-testid="selectPathologyContainer">
        <PathologySelector
          patient={patient}
          selectedPathologyId={value.pathologyId}
          onSelectPathology={handleSelectPathology}
          t={t}
          onNewCondition={() => handleSelectCondition(NO_CONDITION)}
          isForClinicalTrials={isForClinicalTrials}
        />
      </Box>
      {value.pathologyId && (
        <Box data-testid="selectConditionContainer">
          <ConditionSelector
            selectedConditionId={value.conditionId}
            conditions={conditions}
            onSelectCondition={handleSelectCondition}
            t={t}
            selectedPathologyId={value.pathologyId}
            isForClinicalTrials={isForClinicalTrials}
          />
        </Box>
      )}
      {value.conditionId === NO_CONDITION && value.pathologyId === null && (
        <Box sx={{mb: 2}} data-testid="selectBodySiteContainer">
          <Box
            sx={{
              mt: 2
            }}
          >
            <Typography
              variant="h4"
              align="center"
              sx={{
                mb: '16px'
              }}
            >
              {t('shared/common:diagnosticReport.form.selectBodySite')}
            </Typography>
          </Box>
          <BodySiteSelector
            id="bodySite"
            name="bodySite"
            onChange={handleSelectBodySite}
            value={value.bodySiteId}
            bodySites={bodySites}
            property="id"
            label={t('ui:diagnosticReport.bodySiteFieldLabel')}
          />
        </Box>
      )}
      {VisitIdentifierComponent && (
        <Box
          sx={{
            mt: 4
          }}
        >
          <VisitIdentifierComponent
            onChange={handleChangeExtraComponent}
            value={value.visitIdentifier}
          />
        </Box>
      )}
      <BottomBar
        activeStep={0}
        isBackEnabled={false}
        isNextEnabled={Boolean(
          (value.conditionId && value.conditionId !== NO_CONDITION) ||
          (value.conditionId === NO_CONDITION && value.bodySiteId)
        )}
        onNext={handleNext}
        t={t}
        totalSteps={5}
      />
    </Box>
  );
}
